/**
 * Search across every project in a World (RFC 0336 S2).
 *
 * Each project already keeps a zero-hydration catalog of what it holds:
 * `documents`, `sessionMeta`, `tasks` and `apps`. Searching the World is
 * reading those catalogs side by side, never opening a document or a session.
 * So it is cheap enough for ⌘K to run on every keystroke and for an agent to
 * call without thinking about cost.
 *
 * The match is deliberately plain: a case-insensitive substring of the title,
 * then of the path. Ranking puts a title that STARTS with the text first, so
 * "0336" finds "RFC 0336" before a document that merely mentions it in its
 * path, and recency breaks ties. An empty query is "what moved most recently",
 * which is what an empty ⌘K shows.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { projectString } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import { isSessionDone, registryStatus } from '../session/reads'
import { str } from '../stored-string'
import { projectWorldProjects } from './reads'

export type WorldResultKind = 'document' | 'session' | 'task' | 'app'

/** One thing a search found. The id is enough to act on it (RFC 0336 3a);
 *  the project rides along so a reader can say where it was found. */
export interface WorldResult {
  id: string
  title: string
  kind: WorldResultKind
  projectId: string
  /** The project's display name, for "found in Homebase". */
  project: string
  /** A path, a status: whatever tells two same-titled rows apart. */
  hint?: string
  /** Millis, for recency ordering. */
  updatedAt: number
}

export interface WorldSearchQuery {
  text?: string
  kind?: WorldResultKind
  /** Default 20. */
  limit?: number
  /** Opaque: the `cursor` a previous page returned. */
  cursor?: string
}

export interface WorldSearchPage {
  results: WorldResult[]
  /** Present when there is more to read. */
  cursor?: string
}

/** A project's catalog, already read, and what to call it. */
export interface CatalogSource {
  projectId: string
  name: string
  doc: ProjectDoc | undefined
}

const DEFAULT_LIMIT = 20
const MAX_LIMIT = 100

function millis(v: unknown): number {
  if (typeof v === 'number') return v
  const text = projectString(v)
  if (text !== undefined) {
    const n = Date.parse(text)
    return Number.isNaN(n) ? 0 : n
  }
  return 0
}

/** Every row a project's catalog can produce, unfiltered. */
export function catalogRows(source: CatalogSource): WorldResult[] {
  const { projectId, name, doc } = source
  if (!doc) return []
  const base = { projectId, project: name }
  const rows: WorldResult[] = []
  for (const [id, ref] of Object.entries(doc.documents ?? {})) {
    rows.push({
      ...base,
      id,
      kind: 'document',
      // Through `str`: a catalog title and path are stored as one op each
      // rather than one per character, so they come back as wrappers, and an
      // empty one must still read as falsy for the fallbacks below.
      title: str(ref.title) || str(ref.path) || '(untitled)',
      hint: str(ref.path),
      updatedAt: millis(ref.updatedAt),
    })
  }
  const meta = doc.sessionMeta ?? {}
  for (const rawId of doc.sessionIds ?? []) {
    const id = projectString(rawId)
    if (!id) continue
    // A session filed as done is still findable by name, but it is not what
    // anyone means by "what is going on", so it ranks after the open ones.
    const m = meta[id]
    const done = isSessionDone({ ...m, status: registryStatus(m, Date.now()) })
    rows.push({
      ...base,
      id,
      kind: 'session',
      // Through `str`: a registry title is stored as one op rather than one
      // per character, and an empty one must still read as falsy here.
      title: str(m?.title) || '(untitled)',
      ...(done ? { hint: 'done' } : {}),
      updatedAt: done ? 0 : millis(m?.updatedAt),
    })
  }
  for (const [id, t] of Object.entries(doc.tasks ?? {})) {
    rows.push({
      ...base,
      id,
      kind: 'task',
      // Through `str`, as the catalog and registry titles above.
      title: str(t.title) || '(untitled)',
      hint: str(t.status),
      updatedAt: str(t.status) === 'complete' ? 0 : millis(t.updatedAt),
    })
  }
  for (const [id, a] of Object.entries(doc.apps ?? {})) {
    rows.push({
      ...base,
      id,
      kind: 'app',
      title: String(a.name || id),
      updatedAt: 0,
    })
  }
  return rows
}

/** 0 for a title that starts with the text, 1 contains it, 2 the hint does. */
function rank(row: WorldResult, needle: string): number | null {
  if (!needle) return 0
  const title = row.title.toLowerCase()
  if (title.startsWith(needle)) return 0
  if (title.includes(needle)) return 1
  if (row.hint?.toLowerCase().includes(needle)) return 2
  return null
}

/** Search already-read catalogs. Pure, so ⌘K and the agent share it. */
export function searchCatalogs(
  sources: CatalogSource[],
  query: WorldSearchQuery = {},
): WorldSearchPage {
  const needle = (query.text ?? '').trim().toLowerCase()
  const limit = Math.min(
    Math.max(1, Math.floor(query.limit ?? DEFAULT_LIMIT)),
    MAX_LIMIT,
  )
  const offset = Math.max(0, Number.parseInt(query.cursor ?? '0', 10) || 0)
  const ranked: Array<{ row: WorldResult; r: number }> = []
  for (const source of sources) {
    for (const row of catalogRows(source)) {
      if (query.kind && row.kind !== query.kind) continue
      const r = rank(row, needle)
      if (r !== null) ranked.push({ row, r })
    }
  }
  ranked.sort(
    (a, b) =>
      a.r - b.r ||
      b.row.updatedAt - a.row.updatedAt ||
      a.row.title.localeCompare(b.row.title),
  )
  const page = ranked.slice(offset, offset + limit).map((x) => x.row)
  const next = offset + limit
  return {
    results: page,
    ...(next < ranked.length ? { cursor: String(next) } : {}),
  }
}

async function readDoc<T>(
  repo: Repo,
  id: string,
  timeoutMs: number,
): Promise<T | undefined> {
  try {
    const handle = await Promise.race([
      repo.find<T>(id as AnyDocumentId),
      new Promise<null>((r) => setTimeout(() => r(null), timeoutMs)),
    ])
    if (!handle) return undefined
    await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
    return handle.doc() as T | undefined
  } catch {
    // A project this peer cannot open is not part of what it can search.
    return undefined
  }
}

/**
 * The catalogs a search reads: every project the World lists, plus `home` when
 * the World does not list it (a session in a project nobody added). A project
 * that will not open is skipped, never reported as empty.
 */
export async function worldCatalogs(
  repo: Repo,
  worldId: string | null | undefined,
  home: string,
  opts: { timeoutMs?: number } = {},
): Promise<CatalogSource[]> {
  const timeoutMs = opts.timeoutMs ?? 2_000
  const refs = worldId
    ? projectWorldProjects(await readDoc(repo, worldId, timeoutMs))
    : []
  const ids = refs.map((r) => r.projectId)
  if (!ids.includes(home)) ids.unshift(home)
  const names = new Map(refs.map((r) => [r.projectId, r.displayName]))
  const docs = await Promise.all(
    ids.map((id) => readDoc<ProjectDoc>(repo, id, timeoutMs)),
  )
  return ids
    .map((projectId, i) => ({
      projectId,
      name: names.get(projectId) || docs[i]?.title || projectId.slice(0, 8),
      doc: docs[i],
    }))
    .filter((s) => s.doc !== undefined)
}

/** Read the catalogs and search them. */
export async function searchWorld(
  repo: Repo,
  worldId: string | null | undefined,
  home: string,
  query: WorldSearchQuery = {},
): Promise<WorldSearchPage> {
  return searchCatalogs(await worldCatalogs(repo, worldId, home), query)
}

/** How long a mirrored session status is believed (RFC 0284 S8a). */
const STATUS_FRESH_MS = 10 * 60_000

/**
 * The state of the whole World, as a block for the World agent's turn
 * (RFC 0336 3d).
 *
 * Without it the World agent answers from what is on screen, which is one
 * project at most. This is the same zero-hydration read search makes: per
 * project, the sessions that are doing something or asking, the open tasks,
 * and what changed most recently. Bounded, so a large World costs a fixed few
 * hundred words, and the agent searches for anything past it.
 */
export function formatWorldOverview(
  sources: CatalogSource[],
  now = Date.now(),
  opts: { sessions?: number; tasks?: number; documents?: number } = {},
): string {
  const maxSessions = opts.sessions ?? 6
  const maxTasks = opts.tasks ?? 6
  const maxDocs = opts.documents ?? 4
  const blocks: string[] = []
  for (const { projectId, name, doc } of sources) {
    if (!doc) continue
    const meta = doc.sessionMeta ?? {}
    const live = (doc.sessionIds ?? [])
      .map(projectString)
      .filter((id): id is string => !!id)
      .filter((id) => meta[id]?.completedAt == null)
      .map((id) => {
        const m = meta[id] as
          | {
              title?: string
              updatedAt?: number
              status?: string
              statusAt?: number
            }
          | undefined
        const fresh = m?.statusAt != null && now - m.statusAt < STATUS_FRESH_MS
        const status =
          fresh && projectString(m?.status) !== 'idle'
            ? (projectString(m?.status) ?? '')
            : ''
        return {
          id,
          title: projectString(m?.title) || '(untitled)',
          status,
          at: m?.updatedAt ?? 0,
        }
      })
      .sort(
        (a, b) =>
          Number(Boolean(b.status)) - Number(Boolean(a.status)) || b.at - a.at,
      )
      .slice(0, maxSessions)
    const tasks = Object.entries(doc.tasks ?? {})
      .filter(([, t]) => {
        const status = projectString(t.status)
        return status !== 'complete' && status !== 'cancelled'
      })
      .sort(([, a], [, b]) => millis(b.updatedAt) - millis(a.updatedAt))
      .slice(0, maxTasks)
    const docs = Object.entries(doc.documents ?? {})
      .sort(([, a], [, b]) => millis(b.updatedAt) - millis(a.updatedAt))
      .slice(0, maxDocs)
    const lines = [`## ${name} (${projectId})`]
    if (live.length) {
      lines.push('Sessions:')
      for (const s of live)
        lines.push(`- ${s.id}  ${s.title}${s.status ? `  [${s.status}]` : ''}`)
    }
    if (tasks.length) {
      lines.push('Open tasks:')
      for (const [id, t] of tasks)
        lines.push(`- ${id}  ${t.title}  [${t.status}]`)
    }
    if (docs.length) {
      lines.push('Recently changed:')
      for (const [id, d] of docs) lines.push(`- ${id}  ${d.title || d.path}`)
    }
    blocks.push(lines.join('\n'))
  }
  if (blocks.length === 0) return ''
  return [
    '<world>',
    'Every project in this World, as of this turn. Answer about all of it, not only what is on screen; search for anything not listed.',
    ...blocks,
    '</world>',
  ].join('\n\n')
}
